import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface GatewayConfirmDialogProps {
  open: boolean;
  title: string;
  description?: string;
  impactSummary?: { rpm?: number; routes?: number; consumers?: number };
  destructive?: boolean;
  confirmLabel?: string;
  cancelLabel?: string;
  onConfirm: () => Promise<void> | void;
  onCancel: () => void;
}

export function GatewayConfirmDialog({
  open,
  title,
  description,
  impactSummary,
  destructive = false,
  confirmLabel,
  cancelLabel,
  onConfirm,
  onCancel,
}: GatewayConfirmDialogProps) {
  const { t } = useTranslation();
  const resolvedConfirmLabel = confirmLabel ?? t('gateway.shared.confirm.confirm');
  const resolvedCancelLabel = cancelLabel ?? t('gateway.shared.confirm.cancel');

  const [loading, setLoading] = useState(false);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  // Focus trap + initial focus
  useEffect(() => {
    if (open) {
      cancelRef.current?.focus();
    }
  }, [open]);

  // Esc to close
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
      // Focus trap
      if (e.key === 'Tab' && dialogRef.current) {
        const focusable = dialogRef.current.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey) {
          if (document.activeElement === first) {
            e.preventDefault();
            last?.focus();
          }
        } else {
          if (document.activeElement === last) {
            e.preventDefault();
            first?.focus();
          }
        }
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onCancel]);

  if (!open) return null;

  const handleConfirm = async () => {
    setLoading(true);
    try {
      await onConfirm();
    } finally {
      setLoading(false);
    }
  };

  const confirmColor = destructive ? 'var(--color-danger)' : 'var(--color-brand-primary)';

  return (
    <div
      role="presentation"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15, 23, 42, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2000,
      }}
      onClick={onCancel}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="gateway-confirm-title"
        aria-describedby={description ? 'gateway-confirm-desc' : undefined}
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--color-surface-background)',
          borderRadius: 'var(--radius-lg)',
          padding: 'var(--spacing-6)',
          minWidth: '400px',
          maxWidth: '560px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
        }}
      >
        <h2
          id="gateway-confirm-title"
          style={{ margin: 0, fontSize: 'var(--font-size-lg)', color: destructive ? 'var(--color-danger)' : 'var(--color-text-primary)' }}
        >
          {title}
        </h2>

        {description && (
          <p
            id="gateway-confirm-desc"
            style={{ marginTop: 'var(--spacing-2)', color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-sm)' }}
          >
            {description}
          </p>
        )}

        {impactSummary && (
          <div
            style={{
              marginTop: 'var(--spacing-4)',
              padding: 'var(--spacing-3)',
              background: 'var(--color-surface-muted)',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--color-text-secondary)',
            }}
          >
            <strong style={{ color: 'var(--color-text-primary)' }}>{t('gateway.shared.confirm.impact')}</strong>
            <ul style={{ margin: 'var(--spacing-2) 0 0', paddingLeft: 'var(--spacing-4)' }}>
              {impactSummary.rpm !== undefined && <li>{t('gateway.shared.impactRpm', { rpm: impactSummary.rpm })}</li>}
              {impactSummary.routes !== undefined && <li>{t('gateway.shared.impactRoutes', { count: impactSummary.routes })}</li>}
              {impactSummary.consumers !== undefined && <li>{t('gateway.shared.impactConsumers', { count: impactSummary.consumers })}</li>}
            </ul>
          </div>
        )}

        <div
          style={{
            marginTop: 'var(--spacing-6)',
            display: 'flex',
            gap: 'var(--spacing-2)',
            justifyContent: 'flex-end',
          }}
        >
          <button
            ref={cancelRef}
            type="button"
            onClick={onCancel}
            disabled={loading}
            style={{
              padding: 'var(--spacing-2) var(--spacing-4)',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--color-surface-border)',
              background: 'var(--color-surface-background)',
              color: 'var(--color-text-primary)',
              cursor: 'pointer',
              fontSize: 'var(--font-size-sm)',
            }}
          >
            {resolvedCancelLabel}
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={loading}
            aria-busy={loading}
            style={{
              padding: 'var(--spacing-2) var(--spacing-4)',
              borderRadius: 'var(--radius-md)',
              border: 'none',
              background: confirmColor,
              color: 'var(--color-text-inverse)',
              cursor: loading ? 'wait' : 'pointer',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 600,
              opacity: loading ? 0.7 : 1,
            }}
          >
            {loading ? t('gateway.shared.confirm.processing') : resolvedConfirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
